import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AxiosResponse, AxiosError } from "axios";
import { triggerNotification, useGetApiRequests } from "../../../common/CommonExport";
import Loader from "../../../components/Loader/Loader";
import DownloadIcon from "../confidentialityLetter/DownloadIcon";
import {
  DocumentDetail,
  SelectedDownloadDocument,
  DocumentSignerProps,
  GetDocumentDataPayload,
  UploadSignaturePayload
} from "./DocumentSignerTypes";

const DocumentSigner = (props: DocumentSignerProps) => {
  const { getLetterDetails, targetFolderNames } = props;

  const uploadEmployeeSignature = useGetApiRequests("uploadEmployeeSignature", "POST");
  const getDocumentData = useGetApiRequests("documentStatus", "GET");

  const imageBaseUrl = process.env.REACT_APP_IMAGE_VIEW;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const employeeId = searchParams.get("employeeId");
  const documentCode = searchParams.get("documentCode");
  const employeeApiKey = searchParams.get("employeeApiKey");

  const [loader, setLoader] = useState(false);
  const [selectedDownloadDocument, setSelectedDownloadDocument] = useState<SelectedDownloadDocument>({
    signed: null,
    unsigned: null,
    signature: null,
  });


  let isTerms = false;
  let isPrivacyPolicy = false;
  let employeeImageFlag = false;
  let employeeSignatureImage: File | null = null;
  const documentFile: Document = document;


  useEffect(() => {
    const container = document.getElementById("templateDetails");

    if (container && getLetterDetails) {
      container.innerHTML = getLetterDetails;

      setTimeout(() => {
        bindEvents();
        hideErrors();
      }, 300);
      getDocumentStatus();

    }

  }, [getLetterDetails]);
  const bindEvents = () => {
    const submitBtn = documentFile.querySelector(".submit-btn");
    const empImageUpload = documentFile.getElementById("imageUpload");
    const empImagePreview = documentFile.getElementById("imagePreview");
    const terms = documentFile.getElementById("terms");
    const privacyPolicy = documentFile.getElementById("privacy-policy");

    submitBtn?.addEventListener("click", submit);
    empImageUpload?.addEventListener("change", (e) => handleFileSelect(e));
    empImagePreview?.addEventListener("click", () => handleImageReset());
    terms?.addEventListener("change", (e) => handleCheckboxChange(e, "terms"));
    privacyPolicy?.addEventListener("change", (e) => handleCheckboxChange(e, "privacy"));
  };

  const handleFileSelect = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    if (!file.type.match(/image.*/)) {
      target.value = "";
      return;
    }



    const reader = new FileReader();
    reader.onload = (ev: ProgressEvent<FileReader>) => {
      const previewElem = documentFile.getElementById("imagePreview");
      if (previewElem && ev.target) {
        previewElem.innerHTML = `<img src="${ev.target.result}" style="width:200px;height:70px;" />`;
      }

      const signatureHandler = documentFile.getElementById("signatureHandler");
      if (signatureHandler) signatureHandler.innerHTML = "Re-upload Signature";

      const imageError = documentFile.getElementById("imageError");
      if (imageError) imageError.style.display = "none";

      employeeSignatureImage = file;
      employeeImageFlag = true;
    };
    reader.readAsDataURL(file);
  };

  const handleImageReset = () => {
    const previewElem = documentFile.getElementById("imagePreview");
    if (previewElem) previewElem.innerHTML = "";

    const imageUpload = documentFile.getElementById("imageUpload");
    if (imageUpload) (imageUpload as HTMLInputElement).value = "";

    const signatureHandler = documentFile.getElementById("signatureHandler");
    if (signatureHandler) {
      signatureHandler.style.display = "inline-block";
      signatureHandler.innerHTML = "Upload Signature";
    }

    employeeSignatureImage = null;
    employeeImageFlag = false;
  };

  const handleCheckboxChange = (e: Event, type: "terms" | "privacy") => {
    const target = e.target as HTMLInputElement;
    const isChecked = target.checked;

    if (type === "terms") {
      isTerms = isChecked;
      const error = documentFile.getElementById("termsError");
      if (error) error.style.display = isTerms ? "none" : "block";
    } else if (type === "privacy") {
      isPrivacyPolicy = isChecked;
      const error = documentFile.getElementById("policyError");
      if (error) error.style.display = isPrivacyPolicy ? "none" : "block";
    }
  };

  const validateForm = () => {
    let valid = true;

    if (!employeeImageFlag) {
      const error = documentFile.getElementById("imageError");
      if (error) error.style.display = "block";
      valid = false;
    }

    if (!isTerms) {
      const error = documentFile.getElementById("termsError");
      if (error) error.style.display = "block";
      valid = false;
    }

    if (!isPrivacyPolicy) {
      const error = documentFile.getElementById("policyError");
      if (error) error.style.display = "block";
      valid = false;
    }

    return valid;
  };

  const submit = (e: Event) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoader(true);

    const formData = new FormData();
    if (employeeSignatureImage) formData.append("employeeSignature", employeeSignatureImage as Blob);

    const params = {
      employeeId,
      documentCode,
      employeeApiKey,
    };

    uploadEmployeeSignature(formData, {}, params)
      .then((res: AxiosResponse<UploadSignaturePayload> | AxiosError) => {
        setLoader(false);
        const response = res as AxiosResponse<UploadSignaturePayload>;

        if (response?.data?.status?.success?.toLowerCase() === "success") {
          getDocumentStatus();

          triggerNotification(
            "success",
            "",
            response.data.status.message,
            "topRight"
          );

          const signaturePath = response.data.data?.signaturePath;

          if (signaturePath) injectSignedImage(signaturePath);

        } else {
          triggerNotification(
            "error",
            "",
            response?.data?.status?.message || "Upload failed",
            "topRight"
          );
        }
      })
      .catch((err: AxiosError | Error) => {
        setLoader(false);
        triggerNotification("error", "", err.message, "topRight");
      });
  };

  const injectSignedImage = (path: string) => {
    if (!path) return;

    const container = document.getElementById("templateDetails");
    if (!container) return;

    const previewElem = container.querySelector<HTMLDivElement>("#imagePreview");

    if (previewElem) {
      previewElem.innerHTML = `<img src="${imageBaseUrl + path}" style="width:200px;height:70px;" />`;
      previewElem.style.pointerEvents = "none";
    }

    const signatureHandler = container.querySelector<HTMLElement>("#signatureHandler");
    if (signatureHandler) signatureHandler.style.display = "none";

    const uploadInput = container.querySelector<HTMLElement>("#imageUpload");
    if (uploadInput) uploadInput.style.display = "none";

    const submitBtn = container.querySelector<HTMLElement>(".submit-btn");
    if (submitBtn) submitBtn.style.display = "none";

    const termsCheckbox = container.querySelector<HTMLElement>("#terms");
    if (termsCheckbox) termsCheckbox.style.display = "none";

    const policyCheckbox = container.querySelector<HTMLElement>("#privacy-policy");
    if (policyCheckbox) policyCheckbox.style.display = "none";

    const termsError = container.querySelector<HTMLElement>("#termsError");
    if (termsError) termsError.style.display = "none";

    const policyError = container.querySelector<HTMLElement>("#policyError");
    if (policyError) policyError.style.display = "none";

    const imageError = container.querySelector<HTMLElement>("#imageError");
    if (imageError) imageError.style.display = "none";

    const formId = container.querySelector<HTMLElement>("#signupdate");
    if (formId) {
      const privacyPolicyElem = formId.querySelector<HTMLElement>(".privacy-policy");
      if (privacyPolicyElem) privacyPolicyElem.style.display = "none";
    }

    employeeSignatureImage = null;
    employeeImageFlag = false;
  };

  const getDocumentStatus = () => {
    const params = { employeeId, document: "documentDetail" };
    setLoader(true);

    getDocumentData("", {}, params)
      .then((res: AxiosResponse<GetDocumentDataPayload> | AxiosError) => {
        setLoader(false);
        const response = res as AxiosResponse<GetDocumentDataPayload>;

        response?.data?.data?.forEach((item: DocumentDetail) => {
          const folderName = item.document?.folderName?.toLowerCase();

          if (folderName && targetFolderNames.map(n => n.toLowerCase()).includes(folderName)) {

            if (item.name?.toLowerCase().includes('signed') && item.name?.toLowerCase().includes('pdf')) {
              console.log("into doc")
              setSelectedDownloadDocument((prev) => ({
                ...prev,
                signed: item,
              }));
            }
            if (item.documentPath && !item.documentPath.includes("pdf")) {
              injectSignedImage(item.documentPath);
            }
          }
        });
      })
      .catch(() => setLoader(false));
  };

  const hideErrors = () => {
    const ids = [
      "imageError",
      "termsError",
      "policyError"
    ];
    ids.forEach(id => {
      const el = documentFile.getElementById(id);
      if (el) el.style.display = "none";
    });
  };

  return (
    <div>
      {loader && <Loader />}

      <DownloadIcon selectedDownloadDocument={selectedDownloadDocument} />
      <div id="templateDetails" data-testid="templateDetails" />
    </div>
  );
};

export default DocumentSigner;
